import { colorsDendrometer, GREEN, YELLOW, RED, findClosest } from "@helper/colors";

/**
 * Tree state values:
 * 0 = Green (healthy)
 * 1 = Yellow (warning)
 * 2 = Red (critical)
 * 3 = Unknown/Error
 * 4 = Frost
 * 5 = Blue (over-watered)
 */

interface TreeThresholds {
  blue_threshold?: number;
  green_threshold?: number;
  yellow_threshold?: number;
  soil?: {
    pwp?: number;
    max_nf?: number;
    yellow_threshold_swt?: number;
    green_threshold_swt?: number;
    blue_threshold_swt?: number;
  };
}

interface SensorData {
  sensor_state?: number;
  pulse_sensor?: {
    moisture_content?: number;
    frost?: boolean;
  };
  soil_moisture_sensor?: {
    soil_moisture?: number;
  };
  soil_water_tension_sensor?: {
    soil_water_tension?: number;
  };
  dendrometer_sensor?: {
    drought_days?: number;
  };
}

export function statePulse(tree: TreeThresholds, sensor: SensorData): number {
  const moistureContent = sensor?.pulse_sensor?.moisture_content ?? 0;
  const frost = sensor?.pulse_sensor?.frost ?? false;
  if (sensor?.sensor_state !== 0) {
    return 3;
  }
  // moisture_content <= 0 is physically impossible and indicates missing data
  if (moistureContent <= 0) {
    return 3; // Unknown
  }
  if (frost) {
    return 4;
  }
  if (moistureContent > (tree.blue_threshold ?? Infinity)) {
    return 5;
  }
  if (moistureContent > (tree.green_threshold ?? Infinity)) {
    return 0;
  }
  if (moistureContent > (tree.yellow_threshold ?? Infinity)) {
    return 1;
  }
  return 2;
}

export function stateSoilMoisture(tree: TreeThresholds, sensor: SensorData): number {
  const soil = tree.soil;
  const pwp = soil?.pwp ?? 10;
  const maxNf = soil?.max_nf ?? 45;
  const yellowThreshold = pwp;
  const greenThreshold = soil ? pwp + ((maxNf / 2) * (maxNf - pwp)) / maxNf : 25;
  const blueThreshold = maxNf;
  const value = sensor?.soil_moisture_sensor?.soil_moisture ?? 0;
  if (sensor?.sensor_state !== 0) {
    return 3;
  }
  if (value > blueThreshold) {
    return 5;
  }
  if (value > greenThreshold) {
    return 0;
  }
  if (value > yellowThreshold) {
    return 1;
  }
  return 2;
}

export function stateSoilWaterTension(tree: TreeThresholds, sensor: SensorData): number {
  const soil = tree.soil;
  const yellowThreshold = soil?.yellow_threshold_swt
    ? -Math.pow(10, soil.yellow_threshold_swt) / 10
    : -120;
  const greenThreshold = soil?.green_threshold_swt
    ? -Math.pow(10, soil.green_threshold_swt) / 10
    : -60;
  const blueThreshold = soil?.blue_threshold_swt ? -Math.pow(10, soil.blue_threshold_swt) / 10 : -6;
  const value = sensor?.soil_water_tension_sensor?.soil_water_tension ?? 0;
  if (sensor?.sensor_state !== 0) {
    return 3;
  }
  if (value > blueThreshold) {
    return 5;
  }
  if (value > greenThreshold) {
    return 0;
  }
  if (value > yellowThreshold) {
    return 1;
  }
  return 2;
}

export function stateDendrometer(sensor: SensorData): number {
  if (!sensor || sensor.sensor_state !== 0) {
    return 3;
  }
  const droughtDays = Number(sensor?.dendrometer_sensor?.drought_days);
  if (!Number.isFinite(droughtDays)) {
    return 3;
  }
  const normalizedDays = Math.max(0, Math.min(14, droughtDays)) / 14;
  const [, color] = findClosest(normalizedDays, colorsDendrometer());
  if (color === GREEN) {
    return 0;
  }
  if (color === YELLOW) {
    return 1;
  }
  if (color === RED) {
    return 2;
  }
  return 3;
}

export function stateTilt(sensor: SensorData): number {
  // Tilt sensors: return healthy (0) if functional, unknown (3) otherwise
  if (!sensor || sensor.sensor_state !== 0) {
    return 3;
  }
  return 0;
}
